import React, { FC } from 'react';
import orderBy from 'lodash/orderBy';
import {
  getImageExtensionFromType,
  getImageTypeFromPath,
  ImageMime,
} from '../../../../../utils/imageTypes';
import { Image } from './Image/Image';
import {
  DEFAULT_IMAGE_TYPES,
  DEFAULT_IMAGE_FILTER,
} from '../../../../../consts';
import { getImageFilenameAndExtension } from '../../../../../utils/getImageFilenameAndExtension';

export interface BreakpointConfig {
  maxScreenWidth: number;
  imageWidth: number;
  densityList: number[];
}

interface Breakpoint {
  maxScreenWidth: number;
  densityList: {
    imageWidth: number;
    imageHeight: number;
    density: number;
  }[];
}

interface ImageSize {
  width: number;
  height: number;
}

interface PictureProps {
  imagePath: string;
  sourceImageSize: ImageSize;
  breakpoints: Breakpoint[];
  isLazyLoad?: boolean;
  dataHook?: string;
  alt?: string;
  onLoad?: () => void;
  onLoadStart?: () => void;
  imageClassName?: string;
  imageTypes?: ImageMime[];
}

interface Source {
  screenWidth: number;
  densityList: {
    src: string;
    imageWidth: number;
    imageHeight: number;
    density: number;
  }[];
  type: ImageMime;
}

export const createBreakpoints = (
  breakpoints: BreakpointConfig[],
  aspectRatio: ImageSize,
): Breakpoint[] =>
  breakpoints.map((breakpoint) => ({
    maxScreenWidth: breakpoint.maxScreenWidth,
    densityList: breakpoint.densityList.map((density) => {
      const imageWidth = Math.ceil(breakpoint.imageWidth * density);
      const imageHeight = Math.ceil(
        imageWidth * (aspectRatio.height / aspectRatio.width),
      );

      return {
        imageWidth,
        imageHeight,
        density,
      };
    }),
  }));

interface ImageURLProps {
  imagePath: string;
  width: number;
  height: number;
  type: ImageMime;
  filters: string;
}

const getScaledImageUrl = ({
  type,
  imagePath,
  width,
  height,
  filters,
}: ImageURLProps): string => {
  const extension = getImageExtensionFromType(type);
  const { filename } = getImageFilenameAndExtension(imagePath);
  const allFilters = encodeURIComponent(`w_${width},h_${height},${filters}`);

  return `${imagePath}/v1/fill/${allFilters}/${filename}.${extension}`;
};

export const Picture: FC<PictureProps> = ({
  imagePath,
  isLazyLoad,
  breakpoints,
  sourceImageSize,
  dataHook,
  alt,
  onLoad,
  onLoadStart,
  imageClassName,
  imageTypes = DEFAULT_IMAGE_TYPES,
}) => {
  const { width: sourceWidth, height: sourceHeight } = sourceImageSize;

  const breakpointsMap = imageTypes.map((type) => {
    return breakpoints.map((breakpoint) => ({
      screenWidth: breakpoint.maxScreenWidth,
      densityList: breakpoint.densityList.map(
        ({ density, imageWidth, imageHeight }) => ({
          src: getScaledImageUrl({
            imagePath,
            type,
            width: imageWidth,
            height: imageHeight,
            filters: DEFAULT_IMAGE_FILTER,
          }),
          imageWidth,
          imageHeight,
          density,
        }),
      ),
      type,
    })) as Source[];
  });

  const sources: Source[] = ([] as Source[]).concat(...breakpointsMap);

  const baseImageType = getImageTypeFromPath(imagePath);
  const baseSource =
    sources.find((source) => source.type === baseImageType) ?? sources[0];
  const baseImage = baseSource.densityList[0];

  const getSources = (): {
    key: string;
    media: string;
    srcSet: string;
    type: ImageMime;
  }[] => {
    const originalMedia = `(min-width: ${
      sources[sources.length - 1].screenWidth + 1
    }px)`;
    const filters = 'al_c,q_90';
    const originalSources = imageTypes.map((type) => {
      return {
        key: `original-${type}`,
        media: originalMedia,
        srcSet: getScaledImageUrl({
          imagePath,
          width: sourceWidth,
          height: sourceHeight,
          type,
          filters,
        }),
        type,
      };
    });

    const result = sources
      .map((source) => ({
        key: `${source.screenWidth}-${source.type}`,
        media: `(max-width: ${source.screenWidth}px)`,
        srcSet: source.densityList
          .map(({ src, density }) => `${src} ${density}x`)
          .join(', '),
        type: source.type,
      }))
      .concat(originalSources);

    return orderBy(result, ['type'], ['desc']);
  };

  return (
    <picture data-hook={dataHook}>
      {getSources().map((sourceProps) => (
        <source {...sourceProps} />
      ))}
      <Image
        className={imageClassName}
        isLazyLoad={isLazyLoad}
        src={baseImage.src}
        alt={alt || ''}
        onLoad={onLoad}
        onLoadStart={onLoadStart}
      />
    </picture>
  );
};
